import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

import { useTrackTiming } from '@/modules/insight/hooks/useTrackTiming';
import { useNavigation } from '@/modules/common/hooks';
import { ProjectServiceConnector } from '@/modules/api/ProjectApiConnector';
import { useLoadProjectV2 } from '@/modules/floorplanV2/hooks/useLoadProject';

import { useFloorplanAdminStore } from '../store';
import { logger } from '@common/logging/logger';

export const useFloorplanAdmin = () => {
  const { projectId } = useParams();
  const { load: loadProjectV2 } = useLoadProjectV2();
  const { goToPortal } = useNavigation();
  const [projectIsLoading, setProjectIsLoading] = useState(false);
  const [alternativesAreLoading, setAlternativesAreLoading] = useState(false);
  const trackTiming = useTrackTiming();

  const getProject = useCallback(
    async (projectId: string) => {
      try {
        setProjectIsLoading(true);
        const project = await ProjectServiceConnector.getInstance().getProject({
          projectId,
        });

        loadProjectV2(project);
      } catch (e) {
        logger.error('failed to go to project', e);
        goToPortal(true);
      }

      setProjectIsLoading(false);
    },
    [setProjectIsLoading, loadProjectV2, goToPortal],
  );

  const getFloorplanGroups = useCallback(
    async (projectId: string) => {
      const startTime = performance.now();
      try {
        setAlternativesAreLoading(true);
        const layoutAlternatives =
          await ProjectServiceConnector.getInstance().getLayoutAlternatives({ projectId });

        useFloorplanAdminStore.getState().setFloorPlanGroups(layoutAlternatives);
      } catch (e) {
        logger.error('failed to get all floorplan groups', e);
      } finally {
        trackTiming('LOAD_FLOORPLAN_GROUPS', startTime);
        setAlternativesAreLoading(false);
      }
    },
    [trackTiming],
  );

  useEffect(() => {
    if (!projectId) return;

    useFloorplanAdminStore.getState().reset();
    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    getProject(projectId);
    getFloorplanGroups(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  return {
    projectIsLoading,
    alternativesAreLoading,
  };
};
